// Karriäransökan — spontanansökningar, frilansare och specialister.
// Går till huvudinkorgen (hey@fenny.se) med bekräftelse till sökanden.

import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { persistLead, captureBlockedLead } from "@/lib/lead-fallback.server";
import { verifyRecaptcha } from "@/lib/recaptcha.server";
import {
  confirmationEmail,
  internalLeadEmail,
  LEAD_INBOX,
  LEAD_NOTIFY,
  sendEmail,
} from "@/lib/email/resend.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

export const CAREER_ROLES = [
  "spontan",
  "frilans",
  "partnerfotograf",
  "manus",
  "3d",
  "klipp",
  "regi",
  "casting",
  "post",
  "filmare",
] as const;

export type CareerRole = (typeof CAREER_ROLES)[number];

export const ROLE_LABELS_SV: Record<CareerRole, string> = {
  spontan: "Spontanansökan",
  frilans: "Frilansare",
  partnerfotograf: "Fotograf (Partner)",
  manus: "Manusskribent",
  "3d": "3D-artist",
  klipp: "Klippare / editor",
  regi: "Regissör",
  casting: "Casting / talangjakt",
  post: "Post-produktion",
  filmare: "Filmare / DOP",
};

export const ROLE_LABELS_EN: Record<CareerRole, string> = {
  spontan: "Spontaneous application",
  frilans: "Freelancer",
  partnerfotograf: "Photographer (Partner)",
  manus: "Scriptwriter",
  "3d": "3D artist",
  klipp: "Editor",
  regi: "Director",
  casting: "Casting / talent scouting",
  post: "Post-production",
  filmare: "Filmmaker / DOP",
};

const careerSchema = z.object({
  name: z.string().trim().max(100).optional().default(""),
  email: z.string().trim().email("Ogiltig e-postadress").max(255),
  phone: z.string().trim().max(40).optional().default(""),
  city: z.string().trim().max(80).optional().default(""),
  role: z.enum(CAREER_ROLES),
  portfolio: z.string().trim().max(500).optional().default(""),
  visual_style: z.string().trim().max(500).optional().default(""),
  message: z.string().trim().max(2000).optional().default(""),
  locale: z.enum(["sv", "en"]).default("sv"),
  token: z.string().min(10).max(4000),
  session_id: z.string().uuid().nullable().optional(),
  visitor_id: z.string().uuid().nullable().optional(),
  source_path: z.string().trim().max(255).optional().nullable(),
  user_agent: z.string().trim().max(500).optional().nullable(),
}).superRefine((data, ctx) => {
  if (data.role !== "partnerfotograf") return;
  if (!data.city) ctx.addIssue({ code: "custom", path: ["city"], message: "Ort eller region krävs" });
  if (!data.portfolio) ctx.addIssue({ code: "custom", path: ["portfolio"], message: "Portfolio krävs" });
  if (!data.visual_style) ctx.addIssue({ code: "custom", path: ["visual_style"], message: "Visuell stil krävs" });
});

export const submitCareer = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => careerSchema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (
      !checkRateLimit({
        scope: "career",
        keys: [ip, data.email],
        limit: 5,
        windowMs: 60 * 60 * 1000,
      })
    ) {
      await captureBlockedLead({ flow: "career", reason: "rate-limit",
        email: data.email,
        name: data.name || null,
        phone: data.phone || null,
        message: data.message || null,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        extra: { role: data.role, portfolio: data.portfolio, visual_style: data.visual_style, locale: data.locale },
      });
      return {
        ok: false as const,
        error:
          data.locale === "en"
            ? "Too many attempts. Please try again later."
            : "För många försök. Försök igen om en stund.",
      };
    }

    // QA-hook: intern end-to-end-test av leadflödet utan att sänka
    // reCAPTCHA-skyddet för riktiga besökare. Token är en hemlighet.
    const testToken = process.env.LEAD_TEST_TOKEN;
    const isInternalTest = Boolean(testToken) && data.token === `test:${testToken}`;

    if (!isInternalTest) {
      const captcha = await verifyRecaptcha(data.token);
      if (captcha.action === "block") {
        await captureBlockedLead({
          flow: "career",
          reason: "recaptcha",
          email: data.email,
          name: data.name || null,
          phone: data.phone || null,
          message: data.message || null,
          source_path: data.source_path ?? null,
          user_agent: data.user_agent ?? null,
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
          extra: { role: data.role, portfolio: data.portfolio, visual_style: data.visual_style, locale: data.locale },
        });
        return {
          ok: false as const,
          error:
            data.locale === "en"
              ? "Verification failed. Please try again."
              : "Verifieringen misslyckades. Försök igen.",
        };
      }
    }

    const roleLabel =
      data.locale === "en" ? ROLE_LABELS_EN[data.role] : ROLE_LABELS_SV[data.role];
    const displayName = data.name?.trim() || data.email;

    const summaryLines = [
      `Roll: ${roleLabel}`,
      data.phone ? `Telefon: ${data.phone}` : null,
      data.city ? `Ort: ${data.city}` : null,
      data.portfolio ? `Portfolio / länk: ${data.portfolio}` : null,
      data.visual_style ? `Visuell stil / nisch / formspråk: ${data.visual_style}` : null,
      data.message ? `\n${data.message}` : null,
    ].filter(Boolean);
    const summary = summaryLines.join("\n");

    const { ok: persisted } = await persistLead(
      "contact_submissions",
      {
        name: data.name || "",
        email: data.email,
        company: null,
        intent: `career:${data.role}`,
        message: summary,
        source_path: data.source_path ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        user_agent: data.user_agent ?? null,
      },
      {
        source: "career",
        email: data.email,
        name: data.name || null,
        source_path: data.source_path ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
      },
    );

    if (!persisted) {
      return {
        ok: false as const,
        error:
          data.locale === "en"
            ? "Could not save your application. Please try again."
            : "Kunde inte spara din ansökan. Försök igen.",
      };
    }

    const internal = internalLeadEmail({
      subject: `Ny karriäransökan — ${roleLabel} — ${displayName}`,
      heading: "Ny karriäransökan",
      fields: {
        Namn: data.name || "—",
        "E-post": data.email,
        Telefon: data.phone || "—",
        Ort: data.city || "—",
        Roll: roleLabel,
        Portfolio: data.portfolio || "—",
        "Visuell stil / nisch / formspråk": data.visual_style || "—",
        Meddelande: data.message || "—",
        Sida: data.source_path || "—",
      },
    });

    const isEn = data.locale === "en";
    const confirm = confirmationEmail({
      name: displayName,
      subject: isEn ? "Thanks — we received your application" : "Tack — vi har tagit emot din ansökan",
      heading: isEn ? "We received your application" : "Vi har tagit emot din ansökan",
      intro: isEn
        ? "Thank you for your interest in Fenny. We review every application and come back to you within one week if there is a match."
        : "Tack för att du är intresserad av Fenny. Vi läser alla ansökningar och återkommer inom en vecka om det finns en match.",
      body: isEn
        ? `Role: ${roleLabel}.`
        : `Roll: ${roleLabel}.`,
    });

    await Promise.allSettled([
      sendEmail({
        to: LEAD_NOTIFY,
        subject: internal.subject,
        html: internal.html,
        replyTo: data.email,
        logContext: { kind: "internal_lead", leadSource: "career", leadEmail: data.email },
      }),
      sendEmail({
        to: data.email,
        subject: confirm.subject,
        html: confirm.html,
        replyTo: LEAD_INBOX,
        logContext: { kind: "lead_confirmation", leadSource: "career", leadEmail: data.email },
      }),
    ]);

    return { ok: true as const };
  });
